import {
  IJupyterHealthExchangeCompleteInvitationRequest,
  IJupyterHealthExchangeCompleteInvitationResponse,
} from "../@types/JupyterHealthExchange";
import { ANONYMOUS_REQUEST, api } from "./api";

interface IApiErrorBody {
  errors: { code: string; message: string }[];
  /**
   * Request id echoed by the API on every error response (EN-5228). The
   * callback page shows it as a reference so support can match a patient's
   * screenshot to the API log lines for that exact request.
   */
  correlationId?: string;
}

function isErrorBody(value: unknown): value is IApiErrorBody {
  return (
    !!value &&
    typeof value === "object" &&
    Array.isArray((value as { errors?: unknown }).errors)
  );
}

// The shared api axios instance has a response interceptor that swallows
// errors and resolves the promise with `error.response`. That means a 4xx
// from the backend lands in `.then` with `response.data` being the
// `ErrorResponse` body. Detect that shape here and throw so callers can
// route through their catch path.
//
// EN-5229: the invitation callback is an anonymous, pre-login page, so the
// call goes to the default API rather than the stored regional one (see
// ANONYMOUS_REQUEST). The US API has no JupyterHealth Exchange configuration
// and rejected these with InvalidHostname before contacting JHE.
async function postOrThrow<TRequest, TResponse>(
  url: string,
  request: TRequest
): Promise<TResponse> {
  const response = await api.post<TResponse | IApiErrorBody>(
    url,
    request,
    ANONYMOUS_REQUEST
  );
  if (isErrorBody(response.data)) {
    throw response.data;
  }
  return response.data as TResponse;
}

export const jupyterHealthExchangeService = {
  async completeInvitation(
    request: IJupyterHealthExchangeCompleteInvitationRequest
  ): Promise<IJupyterHealthExchangeCompleteInvitationResponse> {
    return postOrThrow<
      IJupyterHealthExchangeCompleteInvitationRequest,
      IJupyterHealthExchangeCompleteInvitationResponse
    >("/Mobile/JupyterHealthExchange/CompleteInvitation", request);
  },
};
